import { AddressSchema, formatAddress, states } from '@buyerflo/core/schemas/generic'
import { ChevronDown as CaretDown } from '@tamagui/lucide-icons-2'
import { useMapsLibrary } from '@vis.gl/react-google-maps'
import { useEditMode } from 'purchasers/editMode'
import { useEffect, useRef, useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Select, SizableText, View, YStack } from 'tamagui'
import { useDebounce } from 'use-debounce'
import { AutocompleteProps, SlotAutocomplete } from './autocomplete'
import Input, { SlotInput, SlotWrapper } from './input'
import { useShowSnack } from './snackbar'
import { MobileFriendlyStack } from './stack'

interface LocationOption {
    description: string
    placeId: string
}

interface LocationAutocompleteProps extends Omit<
    AutocompleteProps<LocationOption, false, false, false>,
    'error' | 'options' | 'value' | 'onChange'
> {
    name: string
    control: any
    disabled?: boolean
    allowManual?: boolean
    geoName?: string
}

const LocationAutocomplete = (props: LocationAutocompleteProps) => {
    const placesLib = useMapsLibrary('places')
    const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
    const showSnack = useShowSnack()
    const formContext = useFormContext()
    const { editMode } = useEditMode()

    const [internalVal, setInternalVal] = useState<string | null>(null)
    const [suggestions, setSuggestions] = useState<google.maps.places.PlacePrediction[]>([])
    const [loading, setLoading] = useState(false)

    const isManual = useWatch({ control: props.control, name: `${props.name}.manual` }) ?? false

    const [debouncedInternalVal] = useDebounce(internalVal, 200)

    const getSessionToken = () => {
        if (!sessionTokenRef.current && placesLib) {
            sessionTokenRef.current = new placesLib.AutocompleteSessionToken()
        }
        return sessionTokenRef.current
    }

    const getSuggestions = async (input: string): Promise<google.maps.places.PlacePrediction[]> => {
        if (!placesLib) return []
        try {
            const { suggestions } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
                input,
                sessionToken: getSessionToken()!,
                includedPrimaryTypes: ['street_address', 'subpremise'],
                includedRegionCodes: ['au'],
            })
            return suggestions
                .map(s => s.placePrediction)
                .filter((p): p is google.maps.places.PlacePrediction => p != null)
        } catch {
            return []
        }
    }

    useEffect(() => {
        if (debouncedInternalVal && placesLib) {
            getSuggestions(debouncedInternalVal).then(setSuggestions)
        } else {
            setSuggestions([])
        }
    }, [debouncedInternalVal, placesLib])

    const getPlaceDetails = async (placeId: string) => {
        if (!placesLib) throw new Error('Google Maps library not loaded')
        const place = new placesLib.Place({ id: placeId })
        await place.fetchFields({ fields: ['addressComponents', 'formattedAddress', 'location'] })
        // Reset session token after fetching details (end of session)
        sessionTokenRef.current = null
        return place
    }

    const handleToggleMode = (e: React.MouseEvent) => {
        e.preventDefault()
        if (!isManual) {
            formContext.setValue(props.name, { manual: true })
        } else {
            formContext.setValue(props.name, undefined)
        }
    }

    if (!formContext) return null

    // If we're in a form context, use Controller
    return (
        <Controller
            name={props.name}
            control={props.control}
            disabled={props.disabled}
            render={({ field }) => {
                // Update internal state when form value changes
                useEffect(() => {
                    if (field.value) {
                        setInternalVal(formatAddress(field.value, 'full'))
                    }
                }, [field.value])

                if (!editMode) {
                    return (
                        <Input
                            label="Address"
                            value={field.value ? formatAddress(field.value, 'full') : ''}
                            disabled={props.disabled}
                            required={props.required}
                        />
                    )
                }

                return (
                    <YStack gap="$2">
                        {!isManual ? (
                            <SlotAutocomplete
                                {...props}
                                field={field}
                                formState={formContext.formState}
                                options={
                                    suggestions.map(prediction => ({
                                        description: prediction.text?.text ?? '',
                                        placeId: prediction.placeId ?? '',
                                    }))
                                }
                                onInputChange={(e: any) => {
                                    if (e) {
                                        setInternalVal(e.target.value)
                                    }
                                }}
                                onBlur={() =>
                                    setInternalVal(field.value ? formatAddress(field.value, 'full') : null)
                                }
                                getOptionLabel={(option: any) => {
                                    if (typeof option === 'string') return option
                                    if (option.streetNumber) return formatAddress(option as any, 'full')
                                    return option.description
                                }}
                                isOptionEqualToValue={(option: LocationOption, value: any) =>
                                    value?.description
                                        ? option.description.replace(', Australia', '') === value.description
                                        : value?.streetName
                                            ? option.description.includes(value.streetName)
                                            : false
                                }
                                onChange={async (e, v) => {
                                    if (isManual) return

                                    const placeId = v?.placeId ?? null

                                    if (placeId && placesLib) {
                                        let place = await getPlaceDetails(placeId)
                                        const getGeoProp = (place: google.maps.places.Place, prop: string) =>
                                            place.addressComponents?.find(c => c.types.includes(prop))
                                                ?.shortText ?? undefined

                                        if (getGeoProp(place, 'street_number') == undefined) {
                                            const retryResults = await getSuggestions('unit ' + debouncedInternalVal)
                                            if (retryResults[0]?.placeId) {
                                                place = await getPlaceDetails(retryResults[0].placeId)
                                            } else {
                                                showSnack('Please enter a valid address', 'danger')
                                                return
                                            }
                                        }

                                        const newAddress: AddressSchema = {
                                            unitNumber: getGeoProp(place, 'subpremise')!,
                                            streetNumber: getGeoProp(place, 'street_number')!,
                                            streetName: getGeoProp(place, 'route')!,
                                            suburb: getGeoProp(place, 'locality')!,
                                            state: getGeoProp(place, 'administrative_area_level_1') as any,
                                            postcode: getGeoProp(place, 'postal_code')!,
                                            manual: false,
                                        }
                                        field.onChange(newAddress)
                                    }
                                }}
                                loading={loading}
                            />
                        ) : (
                            <>
                                <MobileFriendlyStack>
                                    <Controller
                                        name={`${props.name}.unitNumber`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Unit Number (optional)"
                                                {...field}
                                            />
                                        )}
                                    />
                                </MobileFriendlyStack>
                                <MobileFriendlyStack>
                                    <Controller
                                        name={`${props.name}.streetNumber`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Street Number"
                                                {...field}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`${props.name}.streetName`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Street Name"
                                                {...field}
                                                required
                                            />
                                        )}
                                    />
                                </MobileFriendlyStack>
                                <MobileFriendlyStack>
                                    <Controller
                                        name={`${props.name}.suburb`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Suburb"
                                                {...field}
                                            />
                                        )}
                                    />
                                    <Controller
                                        name={`${props.name}.state`}
                                        control={props.control}
                                        render={field => (
                                            <SlotWrapper
                                                {...field}
                                                label="State"
                                            >
                                                <Select
                                                    value={field.field.value ?? ''}
                                                    onValueChange={(value: string) =>
                                                        field.field.onChange(value)
                                                    }
                                                    disablePreventBodyScroll
                                                >
                                                    <Select.Trigger
                                                        width="100%"
                                                        height={44}
                                                        borderRadius="$4"
                                                        backgroundColor="$backgroundStrong"
                                                        iconAfter={<CaretDown size={16} />}
                                                    >
                                                        <Select.Value placeholder="Select state..." />
                                                    </Select.Trigger>
                                                    <Select.Content>
                                                        <Select.Viewport zIndex={200000}>
                                                            {states.map((state, index) => (
                                                                <Select.Item
                                                                    key={state}
                                                                    value={state}
                                                                    index={index}
                                                                >
                                                                    <Select.ItemText>{state}</Select.ItemText>
                                                                </Select.Item>
                                                            ))}
                                                        </Select.Viewport>
                                                    </Select.Content>
                                                </Select>
                                            </SlotWrapper>
                                        )}
                                    />
                                </MobileFriendlyStack>
                                <MobileFriendlyStack>
                                    <Controller
                                        name={`${props.name}.postcode`}
                                        control={props.control}
                                        render={field => (
                                            <SlotInput
                                                label="Postcode"
                                                {...field}
                                            />
                                        )}
                                    />
                                </MobileFriendlyStack>
                            </>
                        )}
                        {props.allowManual && editMode && (
                            <View paddingBottom="$3">
                                <SizableText
                                    cursor="pointer"
                                    color="$primary"
                                    textDecorationLine="underline"
                                    onPress={handleToggleMode}
                                    alignSelf="flex-start"
                                >
                                    {isManual ? 'Use Address Autocomplete' : 'Enter Address Manually'}
                                </SizableText>
                            </View>
                        )}
                    </YStack>
                )
            }}
        />
    )
}

export default LocationAutocomplete
